import React, { useState, useEffect } from 'react';
import { Save, Mail, Clock, AlertCircle, RefreshCw, Loader2, CheckCircle2, Sun, Moon, Monitor, Palette, Cpu, ChevronDown, ChevronRight, Paperclip } from 'lucide-react';
import { useSettings } from '../hooks/useSettings';
import { useTheme } from '../hooks/useTheme';
import { apiCall, default as API_CONFIG } from '../config/api';
import SchwabConnection from './SchwabConnection';
import PushNotifications from './PushNotifications';

// Group tasks by their `group` field, preserving first-seen order for both
// groups and tasks. Returns [[groupName, tasks[]], ...].
const groupTasks = (tasks) => {
  const order = [];
  const byGroup = {};
  for (const t of tasks) {
    const g = t.group || 'Other';
    if (!byGroup[g]) { byGroup[g] = []; order.push(g); }
    byGroup[g].push(t);
  }
  return order.map((g) => [g, byGroup[g]]);
};

// Compact USD formatting: whole dollars show no decimals, otherwise up to 2.
const fmtPrice = (n) => {
  if (n == null) return null;
  const num = Number(n);
  return Number.isInteger(num) ? `$${num}` : `$${num.toFixed(2)}`;
};

// A model's dropdown label, with per-1M input/output pricing when known.
const modelLabel = (m) => {
  const base = m.name || m.id;
  if (m.inputPerMillion == null && m.outputPerMillion == null) return base;
  const input = fmtPrice(m.inputPerMillion) ?? '—';
  const output = fmtPrice(m.outputPerMillion) ?? '—';
  return `${base}  ·  ${input} / ${output} per 1M`;
};

// Render <option>s for the model list. If a value is already saved but no
// longer in the live list (e.g. a retired id), keep it selectable so the form
// doesn't silently drop it.
const renderModelOptions = (models, currentValue) => {
  const ids = new Set(models.map(m => m.id));
  const opts = models.map(m => (
    <option key={m.id} value={m.id}>{modelLabel(m)}</option>
  ));
  if (currentValue && !ids.has(currentValue)) {
    opts.unshift(
      <option key={currentValue} value={currentValue}>{currentValue} (not in current list)</option>
    );
  }
  return opts;
};

const Settings = () => {
  const { settings, setSettings, loading, saving, error: hookError, save } = useSettings();
  const { theme, setTheme } = useTheme();
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [models, setModels] = useState([]);
  const [modelsError, setModelsError] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [showOverrides, setShowOverrides] = useState(false);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      apiCall(API_CONFIG.endpoints.models).then((d) => d?.models || []),
      apiCall(API_CONFIG.endpoints.modelTasks).then((d) => d?.tasks || []),
    ])
      .then(([modelList, taskList]) => {
        if (cancelled) return;
        setModels(modelList);
        setTasks(taskList);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to load model catalog', err);
        setModelsError('Could not load the model list.');
      });
    return () => { cancelled = true; };
  }, []);

  const setOverride = (taskKey, value) => {
    setSettings(prev => ({
      ...prev,
      modelOverrides: { ...(prev.modelOverrides || {}), [taskKey]: value },
    }));
  };

  const handleSave = async () => {
    setSaveError(null);
    setSaved(false);
    try {
      await save();
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setSaveError(err?.message || 'Save failed');
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Settings</h1>
          <p>Configure your RussAdvisor preferences</p>
        </div>
      </div>

      {saved && (
        <div style={{
          background: 'rgba(16, 185, 129, 0.1)',
          border: '1px solid var(--success-color)',
          borderRadius: '8px',
          padding: '1rem',
          marginBottom: '2rem',
          color: 'var(--success-color)',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <CheckCircle2 size={20} />
          Settings saved successfully!
        </div>
      )}

      {(hookError || saveError) && (
        <div style={{
          background: 'var(--severity-alert-bg)',
          border: '1px solid var(--severity-alert-border)',
          borderRadius: '8px',
          padding: '1rem',
          marginBottom: '2rem',
          color: 'var(--danger-color)',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <AlertCircle size={20} />
          {saveError || hookError}
        </div>
      )}

      {/* Schwab connection health -- first because a lapsed authorization
          silently staleness-poisons every other number in the app. */}
      <SchwabConnection />

      <PushNotifications
        notifications={settings.notifications}
        onChange={(next) => handleChange('notifications', next)}
      />

      {/* Chat attachment limits */}
      <div className="card settings-card">
        <div className="card-header">
          <h2 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Paperclip size={20} />
            Chat Attachments
          </h2>
        </div>

        <div className="settings-form">
          <p className="settings-hint" style={{ marginTop: 0 }}>
            Ceilings for files attached to AI chat. Values above what the storage and
            model APIs support are clamped automatically.
          </p>

          <div className="settings-field">
            <label className="settings-label">Max files per message</label>
            <input
              type="number"
              min="1"
              max="20"
              className="settings-input"
              value={settings.attachments?.maxPerMessage ?? 10}
              onChange={(e) => handleChange('attachments', {
                ...(settings.attachments || {}),
                maxPerMessage: Number(e.target.value),
              })}
            />
          </div>

          <div className="settings-field">
            <label className="settings-label">Max image size (MB)</label>
            <input
              type="number"
              min="1"
              max="25"
              className="settings-input"
              value={Math.round((settings.attachments?.maxImageBytes ?? 10485760) / 1048576)}
              onChange={(e) => handleChange('attachments', {
                ...(settings.attachments || {}),
                maxImageBytes: Number(e.target.value) * 1048576,
              })}
            />
            <p className="settings-hint">
              Images are downscaled in the browser before upload, so the stored file is
              usually far smaller than this.
            </p>
          </div>

          <div className="settings-field">
            <label className="settings-label">Max PDF size (MB)</label>
            <input
              type="number"
              min="1"
              max="32"
              className="settings-input"
              value={Math.round((settings.attachments?.maxPdfBytes ?? 20971520) / 1048576)}
              onChange={(e) => handleChange('attachments', {
                ...(settings.attachments || {}),
                maxPdfBytes: Number(e.target.value) * 1048576,
              })}
            />
          </div>

          <div className="settings-field">
            <label className="settings-label">Max PDF pages</label>
            <input
              type="number"
              min="1"
              max="100"
              className="settings-input"
              value={settings.attachments?.maxPdfPages ?? 40}
              onChange={(e) => handleChange('attachments', {
                ...(settings.attachments || {}),
                maxPdfPages: Number(e.target.value),
              })}
            />
            <p className="settings-hint">
              Each page costs roughly 2,000 tokens every turn it stays in context.
            </p>
          </div>

          <div className="settings-field">
            <label className="settings-label">Max text/CSV size (MB)</label>
            <input
              type="number"
              min="1"
              max="5"
              className="settings-input"
              value={Math.round((settings.attachments?.maxTextBytes ?? 2097152) / 1048576)}
              onChange={(e) => handleChange('attachments', {
                ...(settings.attachments || {}),
                maxTextBytes: Number(e.target.value) * 1048576,
              })}
            />
          </div>
        </div>
      </div>

      {/* Email Settings */}
      <div className="card settings-card">
        <div className="card-header">
          <h2 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Mail size={20} />
            Email Reports
          </h2>
        </div>

        <div className="settings-form">
          <div className="settings-field">
            <label className="settings-label">Email Address</label>
            <input
              type="email"
              value={settings.emailAddress}
              onChange={(e) => handleChange('emailAddress', e.target.value)}
              className="settings-input"
            />
          </div>

          <div className="settings-field">
            <label className="settings-label">Daily Report Time (EST)</label>
            <input
              type="time"
              value={settings.emailTime}
              onChange={(e) => handleChange('emailTime', e.target.value)}
              className="settings-input"
            />
            <p className="settings-hint">
              <Clock size={14} />
              Currently set to {settings.emailTime} EST
            </p>
          </div>

          <div className="settings-checkbox">
            <input
              type="checkbox"
              id="emailEnabled"
              checked={settings.emailEnabled}
              onChange={(e) => handleChange('emailEnabled', e.target.checked)}
            />
            <label htmlFor="emailEnabled">Enable daily email reports</label>
          </div>
        </div>
      </div>

      {/* Auto-sync */}
      <div className="card settings-card">
        <div className="card-header">
          <h2 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <RefreshCw size={20} />
            Auto Sync
          </h2>
        </div>

        <div className="settings-form">
          <div className="settings-checkbox">
            <input
              type="checkbox"
              id="autoSync"
              checked={settings.autoSync}
              onChange={(e) => handleChange('autoSync', e.target.checked)}
            />
            <label htmlFor="autoSync">Auto-sync portfolio daily at {settings.syncTime} EST</label>
          </div>
        </div>
      </div>

      {/* Appearance */}
      <div className="card settings-card">
        <div className="card-header">
          <h2 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Palette size={20} />
            Appearance
          </h2>
        </div>

        <div className="settings-form">
          <div className="settings-field">
            <label className="settings-label">Theme</label>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <ThemeButton active={theme === 'light'}  onClick={() => setTheme('light')}  icon={<Sun size={16} />}     label="Light" />
              <ThemeButton active={theme === 'dark'}   onClick={() => setTheme('dark')}   icon={<Moon size={16} />}    label="Dark" />
              <ThemeButton active={false}              onClick={() => { localStorage.removeItem('theme'); window.location.reload(); }} icon={<Monitor size={16} />} label="System" />
            </div>
            <p className="settings-hint">
              Stored on this device only -- the choice doesn't sync across browsers.
            </p>
          </div>
        </div>
      </div>

      {/* AI Models */}
      <div className="card settings-card">
        <div className="card-header">
          <h2 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Cpu size={20} />
            AI Models
          </h2>
        </div>

        <div className="settings-form">
          {modelsError && (
            <p className="settings-hint" style={{ color: 'var(--danger-color)' }}>
              <AlertCircle size={14} />
              {modelsError} Pickers still work if you already have a saved value.
            </p>
          )}

          <div className="settings-field">
            <label className="settings-label">Default Model</label>
            <select
              value={settings.defaultModel || ''}
              onChange={(e) => handleChange('defaultModel', e.target.value)}
              className="settings-input"
            >
              <option value="">Use built-in defaults</option>
              {renderModelOptions(models, settings.defaultModel)}
            </select>
            <p className="settings-hint">
              Applied to any task without its own override below. Leave on
              "Use built-in defaults" to keep the app's per-task defaults.
            </p>
          </div>

          <button
            type="button"
            onClick={() => setShowOverrides(v => !v)}
            className="btn btn-secondary"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.375rem', alignSelf: 'flex-start' }}
          >
            {showOverrides ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            Per-task overrides
          </button>

          {showOverrides && groupTasks(tasks).map(([group, groupTaskList]) => (
            <div key={group}>
              <div className="settings-label" style={{ marginTop: '0.75rem', opacity: 0.7, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                {group}
              </div>
              {groupTaskList.map(({ key, label }) => (
                <div className="settings-field" key={key}>
                  <label className="settings-label">{label}</label>
                  <select
                    value={settings.modelOverrides?.[key] || ''}
                    onChange={(e) => setOverride(key, e.target.value)}
                    className="settings-input"
                  >
                    <option value="">Inherit default</option>
                    {renderModelOptions(models, settings.modelOverrides?.[key])}
                  </select>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Save Button */}
      <button
        className="btn btn-primary settings-save-btn"
        onClick={handleSave}
        disabled={saving}
      >
        {saving ? <Loader2 size={18} className="spin" /> : <Save size={18} />}
        {saving ? 'Saving...' : 'Save Settings'}
      </button>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .spin { animation: spin 1s linear infinite; }
      `}</style>
    </div>
  );
};

const ThemeButton = ({ active, onClick, icon, label }) => (
  <button
    type="button"
    onClick={onClick}
    className={`btn ${active ? 'btn-primary' : 'btn-secondary'}`}
    style={{ display: 'inline-flex', alignItems: 'center', gap: '0.375rem' }}
  >
    {icon} {label}
  </button>
);

export default Settings;
